export type ContentType = "field-test" | "insight" | "article" | string;

export interface Post {
  slug: string;
  canonicalUrl: string;
  title: string;
  seoTitle: string;
  description?: string;
  excerpt?: string;
  published: boolean;
  publishedAt?: string;
  updatedAt?: string;
  date?: string;
  author?: string;
  category?: string;
  activity?: string;
  topic?: string;
  tags: string[];
  readingTime?: number;
  image?: string;
  type?: ContentType;
  content?: string;
}

let postIndexPromise: Promise<Post[]> | undefined;
const postPromises = new Map<string, Promise<Post | null>>();

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url, { credentials: "same-origin" });
  if (!response.ok) {
    throw new Error(`Unable to load blog content (${response.status})`);
  }
  return response.json() as Promise<T>;
}

export default function getPosts(): Promise<Post[]> {
  if (!postIndexPromise) {
    postIndexPromise = fetchJson<Post[]>(
      `${process.env.PUBLIC_URL}/generated/blog/index.json`
    );
  }
  return postIndexPromise;
}

export function getPost(slug: string): Promise<Post | null> {
  const normalizedSlug = decodeURIComponent(slug).trim().toLowerCase();
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalizedSlug)) {
    return Promise.resolve(null);
  }
  const existing = postPromises.get(normalizedSlug);
  if (existing) return existing;

  const request = fetchJson<Post>(
    `${process.env.PUBLIC_URL}/generated/blog/${normalizedSlug}.json`
  ).catch((error) => {
    if (error instanceof Error && error.message.includes("(404)")) return null;
    throw error;
  });
  postPromises.set(normalizedSlug, request);
  return request;
}

export function resetPostCacheForTests() {
  postIndexPromise = undefined;
  postPromises.clear();
}
